import React, { useEffect, useState } from "react";
import "./ProductPage.css";
import {
  FaStar,
  FaMinus,
  FaPlus,
  FaTruck,
  FaQuestionCircle,
  FaShareAlt,
  FaClock,
  FaUndo,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import base_url from "../../api";

const ProductPage = () => {
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState("");
  const [button, setButton] = useState("Add to Cart");
  const [cartProducts, setCartProducts] = useState(() => {
    const saved = localStorage.getItem("itemsId");
    return saved ? JSON.parse(saved) : [];
  });

  const [customizations, setCustomizations] = useState({});
  const [selectedStep, setSelectedStep] = useState(0);

  const productID = new URLSearchParams(window.location.search).get("id");

  const [customizationOptions, setCustomizationOptions] = useState([]);


  // All possible customization sets
  const customizationSets = {
    Kurta: [
      { key: "fabric", question: "🧵 What fabric do you prefer?", options: ["Cotton", "Linen", "Silk"] },
      { key: "collar", question: "👔 Which collar style do you like?", options: ["Regular", "Chinese", "Band"] },
      { key: "sleeve", question: "👕 Sleeve length?", options: ["Full", "Half", "Three-quarter"] },
      { key: "color", question: "🎨 Which color do you like?", options: ["White", "Blue", "Maroon", "Cream"] },
    ],
    Pyjama: [
      { key: "fabric", question: "🧵 Choose your fabric for Pyjama:", options: ["Cotton", "Linen", "Silk Blend"] },
      { key: "fit", question: "👖 Preferred Fit?", options: ["Regular Fit", "Slim Fit", "Loose Fit"] },
      { key: "length", question: "📏 Choose length type:", options: ["Full Length", "3/4th", "Above Ankle"] },
      { key: "color", question: "🎨 Select color:", options: ["White", "Cream", "Beige", "Black"] },
    ],
    Shawl: [
      { key: "material", question: "🧶 Choose Shawl Material:", options: ["Wool", "Pashmina", "Silk", "Cashmere"] },
      { key: "pattern", question: "🌸 Select Pattern:", options: ["Plain", "Embroidered", "Printed", "Woven"] },
      { key: "size", question: "📐 Choose Size:", options: ["Standard", "Large", "Extra Large"] },
      { key: "color", question: "🎨 Pick your favorite color:", options: ["Maroon", "Brown", "Grey", "Black"] },
    ],
  };

  // Fetch product
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${base_url}/products/${productID}`);
        const data = await res.json();
        setProduct(data);
        // Set customization questions based on subcategory
        const sub = data.subcategory?.toLowerCase();

        if (sub.includes("kurta")) setCustomizationOptions(customizationSets.Kurta);
        else if (sub.includes("pyjama") || sub.includes("pajama")) setCustomizationOptions(customizationSets.Pyjama);
        else if (sub.includes("shawl")) setCustomizationOptions(customizationSets.Shawl);
        else setCustomizationOptions([]); // No customization

        setSelectedSize(data.size?.split(",")[0] || "");

        // Default image (no color-based logic)
        const firstImg = Object.values(data.images)[0]?.[0] || "";
        setMainImage(`${base_url}/images/${firstImg}`);
      } catch (err) {
        console.error("Error fetching product:", err);
      }
    };
    fetchProduct();
  }, [productID]);

  if (!product) return <p>Loading product...</p>;

  const handleSelect = (key, value) => {
    setCustomizations((prev) => ({ ...prev, [key]: value }));
  };

  const handleNextStep = () => {
    const currentKey = customizationOptions[selectedStep].key;
    if (!customizations[currentKey]) {
      toast.warn("Please select an option before continuing!");
      return;
    }

    if (selectedStep < customizationOptions.length - 1) {
      setSelectedStep(selectedStep + 1);
    } else {
      handleAddToCart(); // On last step, add to cart
    }
  };

  const handleAddToCart = () => {
    const numericId = Number(productID);
    let updatedCart;

    const existingIndex = cartProducts.findIndex(
      (item) =>
        item.id === numericId &&
        item.size === selectedSize &&
        JSON.stringify(item.customizations) === JSON.stringify(customizations)
    );


    const cartItem = {
      id: numericId,
      name: product.name,
      brand: product.brand,
      price: product.price,
      size: selectedSize,
      quantity,
      customizations,
    };

    if (existingIndex !== -1) {
      updatedCart = [...cartProducts];
      updatedCart[existingIndex].quantity += quantity;
      toast.info("Quantity updated!", { position: "top-right", autoClose: 2000 });
    } else {
      updatedCart = [...cartProducts, cartItem];
      toast.success("Product added with customization!", { position: "top-right", autoClose: 2000 });
    }

    setCartProducts(updatedCart);
    localStorage.setItem("itemsId", JSON.stringify(updatedCart));
    setButton("Added to Cart");

    console.clear();
    console.log("🧵 PRODUCT ADDED TO CART:");
    console.table({
      ID: numericId,
      Name: product.name,
      Brand: product.brand,
      Price: `C$${product.price}`,
      Size: selectedSize,
      Quantity: quantity,
    });
    console.log("🎨 Customizations Selected:");
    console.log(customizations);
    console.log("🛒 Full Cart Array:", updatedCart);
  };

  const current = customizationOptions[selectedStep];

  return (
    <div className="product-detailing">
      <div className="product-container">
        {/* Left: Gallery */}
        <div className="gallery">
          <div className="thumbnails">
            {Object.values(product.images).flat().map((img, i) => (
              <img
                key={i}
                src={`${base_url}/images/${img}`}
                alt={product.name}
                onClick={() => setMainImage(`${base_url}/images/${img}`)}
              />
            ))}
          </div>
          <div
            className="main-image"
            style={{
              backgroundImage: `url("${mainImage}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              width: "100%",
              height: "600px",
            }}
          />
        </div>

        {/* Right: Details */}
        <div className="details">
          <p className="category">{product.category}</p>
          <h1 className="title">{product.name}</h1>

          <div className="reviews">
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} />
              ))}
            </div>
            <span>{product.stock} available in stock</span>
          </div>

          <div className="price">
            <span className="current">C${product.price}</span>
          </div>

          <div className="desc">{product.description}</div>

          {/* Sizes */}
          {/* <div className="sizes">
            {product.size && (
              <>
                <p>Size: {selectedSize}</p>
                <div className="size-options">
                  {product.size.split(",").map((s) => (
                    <button
                      key={s}
                      className={`size-btn ${selectedSize === s ? "active" : ""}`}
                      onClick={() => setSelectedSize(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div> */}

          {/* Step-based Customization */}
          {customizationOptions.length > 0 ? (
            <div className="customizer">
              <h2>🧍 Product Customization</h2>
              <p className="question">{customizationOptions[selectedStep].question}</p>

              <div className="choices">
                {customizationOptions[selectedStep].options.map((opt) => (
                  <button
                    key={opt}
                    className={`choice-btn ${customizations[customizationOptions[selectedStep].key] === opt ? "selected" : ""
                      }`}
                    onClick={() =>
                      handleSelect(customizationOptions[selectedStep].key, opt)
                    }
                  >
                    {opt}
                  </button>
                ))}
              </div>

              <div className="navigation">
                <button
                  className="prev-btn"
                  disabled={selectedStep === 0}
                  onClick={() => setSelectedStep(selectedStep - 1)}
                >
                  ← Previous
                </button><div className="quantity">
                  <span>Quantity:</span>
                  <div className="quantity-box">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))}><FaMinus /></button>
                    <span>{quantity}</span>
                    <button onClick={() => setQuantity(quantity + 1)}><FaPlus /></button>
                  </div>
                </div>
                <button className="next-btn" onClick={handleNextStep}>
                  {selectedStep === customizationOptions.length - 1
                    ? "Add to Cart →"
                    : "Next →"}
                </button>
              </div>

              <div className="progress-bar">
                {customizationOptions.map((step, i) => (
                  <div
                    key={step.key}
                    className={`step ${i <= selectedStep ? "active" : ""}`}
                  >
                    <span className="dot">{i + 1}</span>
                    <p>{step.key}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ marginTop: "20px", color: "#555" }}>
              No customization available for this product.
            </p>
          )}



          {/* Product Info */}
          <div className="product-info">
            {/* <div className="product-links">
              <a href="#"><FaTruck /> Delivery & Return</a>
              <a href="#"><FaQuestionCircle /> Ask A Question</a>
              <a href="#"><FaShareAlt /> Share</a>
            </div> */}

            {/* <div className="delivery">
              <FaClock /> Estimated Delivery: <span>3–6 days</span> (India)
            </div>
            <div className="delivery">
              <FaUndo /> Return within <span>30 days</span> of purchase.
            </div>
            <div className="store-link">
              <FaMapMarkerAlt /> <a href="#">View Store Information</a>
            </div> */}

            {/* <div className="meta">
              <p><span>Brand:</span> {product.brand}</p>
              <p><span>Material:</span> {product.material}</p>
              <p><span>Available:</span> {product.stock > 0 ? "In Stock" : "Out of Stock"}</p>
            </div> */}
          </div>
        </div>
      </div>

      {/* Product Details Section */}
      <div className="product-details">
        <div className="details-left">
          <h3>About this product</h3>
          <p>{product.description}</p>
          Material: {product.material} <br />
          Brand: {product.brand} <br />
          Origin: India
        </div>
        <div className="details-right">
          <h3>COMPOSITION, ORIGIN AND CARE</h3>
          <p className="care-text">
            {product.precaution || "MACHINE WASHING MAX 30°C / SHORT SPIN DRY"}
          </p>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default ProductPage;
